/**
 * EngagementSessionListPageV2 — redesign port of the parameterized
 * EngagementSessionListPage (used by both Recommended Sessions and
 * Popular List). Same prop contract as the v1 base so the two thin
 * wrapper pages don't change shape:
 *
 *   <EngagementSessionListPageV2
 *     title="Recommended Sessions"
 *     subtitle="No Interests Selected"
 *     sliceName="recommended_list"
 *     fetchList={fetchRecommendedList}
 *     replaceList={replaceRecommendedList}
 *   />
 *
 * (The v1 base also takes a `description` explainer; the v2 header
 * dropped it per CMS feedback — Monday #12171251310. It is shown again only
 * when a page passes `description` explicitly — the per-topic lists do, since
 * "interleaved" needs explaining.)
 *
 * Layout:
 *   • PageHeader with sticky save / discard actions in the right column.
 *   • Two-column body — sortable session list on the left (LibSection
 *     wrapped card), Add-session picker on the right (RailCard).
 *   • Drag-to-reorder + remove inline, "Add a session" SearchSelect
 *     filters out items already in the list and limits to sessions
 *     that legitimately belong in a curated list (no chapters /
 *     series / challenges / audiobooks).
 *
 * The save button only enables when the working list is dirty; on save
 * we PUT the full ordered list of session_ids via `replaceList`.
 *
 * `embedded` renders the same editor as a section inside another page (the
 * Edit Topic page): a section heading replaces the page header, with the same
 * Save / Discard actions. `onDirtyChange(isDirty)` (optional) tells the host
 * page whether the list has unsaved changes.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import { fetchAllSessions } from '../../../../actions/sessionActions';
import {
  PageHeader,
  Button,
  StatusPill,
  TierPill,
  IconButton,
  cn,
} from '../../../redesign';
import { RailCard, RailCardHead, SearchSelect } from '../../../redesign/edit';

/* ─── Icons ────────────────────────────────────────────────────────── */

const SaveIconSvg = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const DragHandleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ width: 14, height: 14 }}>
    <circle cx="9"  cy="6"  r="0.5" />
    <circle cx="9"  cy="12" r="0.5" />
    <circle cx="9"  cy="18" r="0.5" />
    <circle cx="15" cy="6"  r="0.5" />
    <circle cx="15" cy="12" r="0.5" />
    <circle cx="15" cy="18" r="0.5" />
  </svg>
);

const TrashIcon = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* ─── Parent label helper (same logic as v1 SortableSessionList) ────── */

function getParentLabel(session) {
  if (session.session_is_chapter || session.subcategory_is_book) {
    return session.subcategory_title
      ? `Audiobook: ${session.subcategory_title}`
      : 'Audiobook';
  }
  if (session.challenge_title) return `Challenge: ${session.challenge_title}`;
  if (session.series_title)    return `Series: ${session.series_title}`;
  if (session.subcategory_title) return session.subcategory_title;
  if (session.category_title)    return session.category_title;
  return 'No Category';
}

/* ─── Sortable row — single session card in the working list ────────── */

function SortableSessionRow({ session, index, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: session.session_id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const parentLabel = getParentLabel(session);
  const subtext = `ID#${session.session_id} · ${parentLabel}`;
  const inactive = !session.session_is_active;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={cn(
        'group flex items-center bg-surface border border-sand',
        'transition-shadow duration-150',
      )}
      data-dragging={isDragging ? 'true' : 'false'}
      data-inactive={inactive ? 'true' : 'false'}
    >
      {/* Drag handle */}
      <span
        {...listeners}
        aria-label="Drag to reorder"
        tabIndex={0}
        className={cn(
          'grid place-items-center text-text-muted shrink-0 cursor-grab',
          'hover:text-navy active:cursor-grabbing',
          'focus:outline-none focus-visible:text-navy',
        )}
        style={{
          width: 32,
          height: 64,
          touchAction: 'none',
        }}
      >
        {DragHandleIcon}
      </span>

      {/* Position number */}
      <span
        className="font-mono text-text-muted text-center shrink-0"
        style={{ width: 28, fontSize: 12 }}
      >
        {index + 1}
      </span>

      {/* Cover */}
      <div
        className="bg-cream border border-sand shrink-0"
        style={{
          width: 48,
          height: 48,
          borderRadius: 6,
          marginLeft: 4,
          marginRight: 12,
          backgroundImage: session.session_image
            ? `url('${session.session_image}')`
            : undefined,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />

      {/* Title + meta */}
      <div className="flex-1 min-w-0" style={{ paddingTop: 8, paddingBottom: 8 }}>
        <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
          <span
            className="font-semibold text-navy truncate"
            style={{ fontSize: 13.5 }}
            title={session.session_name}
          >
            {session.session_name}
          </span>
          <TierPill isFree={!!session.session_is_free} />
          {inactive && <StatusPill active={false} />}
        </div>
        <div
          className="text-text-muted truncate"
          style={{ fontSize: 11.5, marginTop: 2 }}
          title={subtext}
        >
          {subtext}
        </div>
      </div>

      {/* Remove */}
      <div
        className="opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        style={{ paddingRight: 8 }}
      >
        <IconButton
          size="sm"
          aria-label={`Remove ${session.session_name}`}
          onClick={() => onRemove(session.session_id)}
        >
          {TrashIcon}
        </IconButton>
      </div>
    </div>
  );
}

/* ─── Add-session picker (redesign SearchSelect, add-and-clear) ───────────── */

function SessionAddPickerV2({ excludeIds, onAdd, disabled }) {
  const dispatch = useDispatch();
  const allSessions = useSelector((s) => s.sessions.all_sessions);

  useEffect(() => {
    if (!allSessions) dispatch(fetchAllSessions());
  }, [dispatch, allSessions]);

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);

  const eligible = useMemo(() => {
    if (!Array.isArray(allSessions)) return [];
    return allSessions.filter((s) => {
      if (excluded.has(s.session_id)) return false;
      if (s.session_is_chapter) return false;
      if (s.challenge_id) return false;
      if (s.series_id) return false;
      if (s.subcategory_id && s.subcategory_is_book) return false;
      return Boolean(s.category_id || s.subcategory_id);
    });
  }, [allSessions, excluded]);

  // Map to SearchSelect's { value, label, sublabel, image } shape.
  const options = useMemo(
    () =>
      eligible.map((s) => {
        const parent = s.subcategory_title || s.category_title || '';
        return {
          value: s.session_id,
          label: s.session_name || '',
          sublabel: [parent, s.author_name].filter(Boolean).join(' · '),
          image: s.session_image || '',
        };
      }),
    [eligible],
  );

  // Add-and-clear: value stays null, so the picker resets after each
  // pick. Look the full session back up by id to hand to onAdd.
  return (
    <SearchSelect
      value={null}
      onChange={(sessionId) => {
        const session = eligible.find((s) => s.session_id === sessionId);
        if (session) onAdd(session);
      }}
      options={options}
      placeholder="Add a session…"
      searchPlaceholder="Search by session name"
      emptyText="No matching sessions"
      clearable={false}
      disabled={disabled}
    />
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function EngagementSessionListPageV2({
  title,
  subtitle,
  description,
  sliceName,
  selectList,
  fetchList,
  replaceList,
  embedded = false,
  onDirtyChange,
}) {
  const dispatch = useDispatch();
  // `selectList` (optional) picks this page's own entry when one slice holds many
  // lists (per-topic lists). Such a list is editable only once it has loaded
  // successfully; the shared Recommended / Popular slices behave as before.
  const listState = useSelector((s) => (selectList ? selectList(s) : s[sliceName]));
  const items = listState?.items;
  const isLoading = listState?.isLoading;
  const hasError = !!listState?.hasError;
  const editable = selectList ? !!listState?.loaded && !hasError : true;

  const [workingList, setWorkingList] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    // The thunk surfaces its own error; swallow the rethrow so it isn't unhandled.
    Promise.resolve(dispatch(fetchList())).catch(() => {});
  }, [dispatch, fetchList]);

  useEffect(() => {
    if (Array.isArray(items) && (!selectList || listState?.loaded)) {
      setWorkingList(items);
      setIsDirty(false);
    } else if (selectList) {
      // Not (or no longer) loaded for this topic: never keep a stale working copy.
      setWorkingList([]);
      setIsDirty(false);
    }
  }, [items, selectList, listState?.loaded]);

  const excludeIds = useMemo(
    () => workingList.map((s) => s.session_id),
    [workingList],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    if (!editable) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWorkingList((prev) => {
      const oldIndex = prev.findIndex((s) => s.session_id === active.id);
      const newIndex = prev.findIndex((s) => s.session_id === over.id);
      if (oldIndex < 0 || newIndex < 0) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
    setIsDirty(true);
  };

  const handleRemove = (sessionId) => {
    if (!editable) return;
    setWorkingList((prev) => prev.filter((s) => s.session_id !== sessionId));
    setIsDirty(true);
  };

  const handleAdd = (session) => {
    if (!editable) return;
    if (workingList.some((s) => s.session_id === session.session_id)) return;
    setWorkingList((prev) => [...prev, session]);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!editable) return;
    try {
      setIsSaving(true);
      const ids = workingList.map((s) => s.session_id);
      await dispatch(replaceList(ids));
      setIsDirty(false);
    } catch {
      // errorHandler already surfaces a Swal — nothing else to do.
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (Array.isArray(items)) {
      setWorkingList(items);
      setIsDirty(false);
    }
  };

  const totalLabel = `${workingList.length.toLocaleString()} in list`;

  const actions = (
    <div className="flex items-center" style={{ gap: 8 }}>
      {isDirty && (
        <Button
          variant="ghost"
          onClick={handleDiscard}
          disabled={isSaving}
          type="button"
        >
          Discard
        </Button>
      )}
      <Button
        variant="coral"
        onClick={handleSave}
        disabled={!editable || !isDirty || isSaving}
        leadingIcon={SaveIconSvg}
        type="button"
      >
        {isSaving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );

  return (
    <div className={embedded ? undefined : 'bg-bg min-h-full'}>
      {embedded ? (
        <div className="flex items-start" style={{ gap: 16, flexWrap: 'wrap', marginBottom: 8 }}>
          <div className="flex-1" style={{ minWidth: 260 }}>
            <h2 className="text-navy" style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
              {title}
              <span className="text-text-muted" style={{ fontSize: 13, fontWeight: 400, marginLeft: 8 }}>
                {totalLabel}
              </span>
            </h2>
            {description && (
              <div className="text-text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>
                {description}
              </div>
            )}
          </div>
          {actions}
        </div>
      ) : (
        <PageHeader
          eyebrow="Engagement & Promotions"
          title={title}
          sub={
            (subtitle || description) && (
              <>
                {subtitle && (
                  <span className="font-condensed font-bold uppercase text-text-muted" style={{ fontSize: 11, letterSpacing: '0.08em', marginRight: 8 }}>
                    {subtitle}
                  </span>
                )}
                {description}
              </>
            )
          }
          total={totalLabel}
          actions={actions}
        />
      )}

      <div
        className="edit-canvas grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] items-start"
        style={{ gap: 24, padding: embedded ? 0 : '0 28px 60px' }}
      >
        {/* Sessions in list — main column */}
        <section style={{ minWidth: 0 }}>
          <div
            className="flex items-center justify-between"
            style={{ padding: '6px 4px 10px' }}
          >
            <div
              className="font-condensed font-bold uppercase text-text-muted"
              style={{ fontSize: 11, letterSpacing: '0.08em' }}
            >
              Sessions in list ({workingList.length})
            </div>
            {isDirty && (
              <span
                className="font-condensed font-bold uppercase text-coral"
                style={{ fontSize: 10.5, letterSpacing: '0.08em' }}
              >
                Unsaved changes
              </span>
            )}
          </div>

          {hasError && !editable ? (
            <div
              className="flex items-center justify-center text-text-muted bg-surface border border-sand"
              style={{ height: 200, borderRadius: 12 }}
            >
              Could not load this list. Reload the page to try again — saving is off until it loads.
            </div>
          ) : (isLoading && !items) || (selectList && !editable) ? (
            <div
              className="flex items-center justify-center text-text-muted bg-surface border border-sand"
              style={{ height: 200, borderRadius: 12 }}
            >
              Loading sessions…
            </div>
          ) : workingList.length === 0 ? (
            <div
              className="bg-surface border border-sand text-text-muted italic text-center"
              style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
            >
              No sessions in this list yet. Use the panel on the right to add some.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={workingList.map((s) => s.session_id)}
                strategy={verticalListSortingStrategy}
              >
                <div
                  className="bg-surface border border-sand overflow-hidden"
                  style={{ borderRadius: 12 }}
                >
                  {workingList.map((session, index) => (
                    <SortableSessionRow
                      key={session.session_id}
                      session={session}
                      index={index}
                      onRemove={handleRemove}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>

        {/* Add picker — rail */}
        <aside
          className="edit-rail"
          style={{
            alignSelf: 'start',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            minWidth: 0,
            paddingTop: 32,
          }}
        >
          <RailCard
            head={({ open, toggle }) => (
              <RailCardHead
                eyebrow="Add a session"
                open={open}
                onToggle={toggle}
              />
            )}
          >
            <div style={{ padding: 14 }}>
              <p
                className="text-text-muted"
                style={{ fontSize: 12, marginTop: 0, marginBottom: 8 }}
              >
                Search for an active session by name. Sessions already in the list are hidden.
              </p>
              <p
                className="text-text-muted"
                style={{ fontSize: 11.5, marginTop: 0, marginBottom: 12 }}
              >
                Only standalone category / subcategory sessions can be added —
                Challenge, Series, Audiobook, and Quick Tap sessions are excluded.
              </p>
              <SessionAddPickerV2 excludeIds={excludeIds} onAdd={handleAdd} disabled={!editable} />
            </div>
          </RailCard>
        </aside>
      </div>
    </div>
  );
}
